/**
 * SyncPreviewModal Component
 *
 * Modal for previewing and confirming tenant sync.
 * Shows rollout targets that will be synced, with included/excluded status.
 */
import React, { useMemo } from 'react';
import { Alert, Button, Modal, Table, Tag, Typography } from '@duxton-web/ui';
import type { TableColumnsType } from '@duxton-web/ui';

import { TenantSyncConfig } from '../config';
import { PREVIEW_MODAL_MESSAGES } from '../constants/messages';
import { useSyncToTenant } from '../hooks';
import { TenantSyncPreviewResponse } from '@/lib/PlannerService';
import { getRolloutTargetName } from '@/utils/rollout';

interface RolloutTargetRow {
  key: string;
  rank: number;
  summary: string;
  excluded: boolean;
  excludeReason?: string;
}

interface SyncPreviewModalProps {
  open: boolean;
  onCancel: () => void;
  variableName: string;
  previewData: TenantSyncPreviewResponse;
  config: TenantSyncConfig;
}

const columns: TableColumnsType<RolloutTargetRow> = [
  {
    title: 'Rank',
    dataIndex: 'rank',
    width: 60,
  },
  {
    title: 'Summary',
    dataIndex: 'summary',
    ellipsis: true,
  },
  {
    title: 'Status',
    dataIndex: 'excluded',
    width: 100,
    render: (excluded: boolean) => (
      <Tag color={excluded ? 'error' : 'success'} size="xsmall">
        {excluded ? 'excluded' : 'included'}
      </Tag>
    ),
  },
  {
    title: 'Exclude Reason',
    dataIndex: 'excludeReason',
    width: 250,
    render: (reason?: string) => (
      <Typography.Metadata type="danger">{reason || '—'}</Typography.Metadata>
    ),
  },
];

export const SyncPreviewModal: React.FC<SyncPreviewModalProps> = ({
  open,
  onCancel,
  variableName,
  previewData,
  config,
}) => {
  const { mutate: syncToTenant, isPending: isSyncing } = useSyncToTenant(
    config,
    {
      onSuccess: () => {
        onCancel();
      },
    },
  );

  const hasPendingReview = !!previewData.destinationPendingRolloutUrl;

  const tableData: RolloutTargetRow[] = useMemo(() => {
    if (!previewData.rollout?.rollouts) return [];
    return previewData.rollout.rollouts.map((target, index) => ({
      key: target.id || `target-${index}`,
      rank: index + 1,
      // Use getRolloutTargetName to generate readable name from constraints
      // Falls back to target.string or generic name
      summary:
        getRolloutTargetName(target.constraints) ||
        target.string ||
        `Target ${index + 1}`,
      excluded: target.excluded,
      excludeReason: target.excluded ? previewData.exclusionReason : undefined,
    }));
  }, [previewData]);

  // Calculate counts from the data
  const totalTargets = tableData.length;
  const excludedTargets = tableData.filter((t) => t.excluded).length;
  const includedTargets = totalTargets - excludedTargets;

  const handleConfirmSync = () => {
    syncToTenant(variableName);
  };

  const handleGoToTenant = () => {
    if (previewData.destinationPendingRolloutUrl) {
      window.open(
        previewData.destinationPendingRolloutUrl,
        '_blank',
        'noopener,noreferrer',
      );
    }
    onCancel();
  };

  return (
    <Modal
      open={open}
      title={`Review rollouts before sending to ${config.name}`}
      width={750}
      onCancel={onCancel}
      onOk={handleConfirmSync}
      okText="Confirm Sync"
      okButtonProps={{ loading: isSyncing, disabled: isSyncing }}
      maskClosable={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Pending review warning */}
        {hasPendingReview && (
          <Alert
            type="warning"
            showIcon
            message={PREVIEW_MODAL_MESSAGES.pendingReview.message}
            description={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Typography.Text>
                  {PREVIEW_MODAL_MESSAGES.pendingReview.description}
                </Typography.Text>
                <Button size="small" type="link" onClick={handleGoToTenant}>
                  View existing pending review
                </Button>
              </div>
            }
          />
        )}

        {/* Filtering info */}
        <Alert
          type="info"
          showIcon
          message={PREVIEW_MODAL_MESSAGES.filteringInfo.message}
          description={PREVIEW_MODAL_MESSAGES.filteringInfo.description}
        />

        {/* Variable not linked warning */}
        {previewData.destinationVariableNotLinked && (
          <Alert
            type="warning"
            showIcon
            message={PREVIEW_MODAL_MESSAGES.variableNotLinked.message}
            description={PREVIEW_MODAL_MESSAGES.variableNotLinked.description}
          />
        )}

        {/* Summary */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography.Text strong>Grab rollout targets</Typography.Text>
          <Typography.Metadata type="secondary">
            {`${includedTargets} included · ${excludedTargets} excluded · ${totalTargets} total`}
          </Typography.Metadata>
        </div>

        {/* Targets table */}
        <Table<RolloutTargetRow>
          columns={columns}
          dataSource={tableData}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          size="small"
          scroll={{ y: 300 }}
        />
      </div>
    </Modal>
  );
};
